import { AgentConversationFormActions } from '@/agent/chat_form/store/slice'
import { WS2ErrorPayloadSchema, WS2ReceivedDataSchema, WS2ReceivedDataType, WS2StatusPayloadSchema } from './ws2.schemas'
import { AgentEventChunkSchema, AgentEventType, ClientAgentEventReadSchema } from '@/agent/events/store/schemas'
import { AgentEventsActions } from '@/agent/events/store/slice'
import { ArtifactVersionStatusType, ArtifactVersionUpdatedSchema } from '@/artifacts/schemas'
import { ArtifactsActions } from '@/artifacts/store/slice'
import { WS2Actions } from './ws2.slice'
import { type AppDispatch, type RootState } from '@/store/store'
import { AgentConversationsActions } from '@/agent/conversations/store/slice'
import { kNewConversationTitle } from '@/chat-common/conversation_list/constants'
import { selectAgentConversationFromId, selectAgentConversationHasActiveContentViewer } from '@/agent/conversations/store/selectors'
import * as Sentry from '@sentry/browser'
import { ClientAgentReferenceSchema } from '@/agent/references/store/schemas'
import { AgentReferencesActions } from '@/agent/references/store/slice'
import { ContentViewerActions } from '@/content-viewer/store/slice'
import { selectActiveConversationId } from '@/chat-common/store/chat-ux.selectors'
import { buildArtifactViewerSession } from '@/artifacts/utils/build-artifact-viewer-session'
import { kErrorGeneratingResponseMessage } from '@/constants/constants-strings'
// Template response analytics handled by template-analytics listener middleware

type WS2EventHandlerStore = {
  dispatch: AppDispatch
  getState: () => RootState
}

export function ws2EventHandler(conversationId: string, event: MessageEvent, handlerStore: WS2EventHandlerStore): void {
  const { dispatch, getState } = handlerStore
  const stringifiedData = event.data
  if (!stringifiedData) {
    console.error('No data received from the Stream event')
    return
  }

  // Parse the string into JSON
  let parsedData
  try {
    parsedData = JSON.parse(stringifiedData)
  } catch (error) {
    console.error('Error parsing string into JSON: ', error)
    return
  }

  // Validate the data
  const validatedData = WS2ReceivedDataSchema.safeParse(parsedData)
  if (!validatedData.success) {
    console.error('Invalid websocket event: ', validatedData.error)
    return
  }

  // Destructure the data
  const { payload, type } = validatedData.data

  // Validate and handle the data
  switch (type) {
    case WS2ReceivedDataType.CONVERSATION_EVENT: {
      const validated = ClientAgentEventReadSchema.safeParse(payload)
      if (!validated.success) {
        console.error('Invalid chat message payload: ', validated.error)
        return
      }

      // Upsert the event into state for rendering

      // Template response analytics now managed by listener middleware

      dispatch(AgentEventsActions.upsertEvents({ conversationId, events: [validated.data] }))

      /**
       * Form clearing logic
       *
       * IF: This is a user_query event
       * THEN:
       * - Set the queryIsSubmitting state to false
       *
       * - IF: the value matches the current form input
       *    - THEN: the Clear the form input
       */
      const agentEventType = validated.data.type
      if (agentEventType === AgentEventType.USER_QUERY) {
        // Mark as no longer submitting
        dispatch(AgentConversationFormActions.setQueryIsSubmitting({ conversationId, isSubmitting: false }))

        // Check and clear form input
        const currentFormInput = getState().agentConversationFormState.forms[conversationId]?.requestParams.input_text ?? ''
        if (currentFormInput === validated.data.value) {
          dispatch(
            AgentConversationFormActions.setInputText({
              conversationId,
              text: '',
            })
          )
        }

        // Generate the conversation title if this is a new conversation
        const currentConversationTitle = selectAgentConversationFromId(getState(), { conversationId })?.title
        if (currentConversationTitle === kNewConversationTitle) {
          dispatch(AgentConversationsActions.generateConversationTitle({ conversationId }))
        }
      }

      if (agentEventType === AgentEventType.STOP_REQUEST_RECEIVED) {
        dispatch(WS2Actions.hideTurnProgress({ conversationId }))
      }

      if (agentEventType === AgentEventType.SERVER_ERROR || agentEventType === AgentEventType.CLIENT_ERROR_EVENT) {
        dispatch(WS2Actions.hideTurnProgress({ conversationId }))
      }

      break
    }

    case WS2ReceivedDataType.CONVERSATION_EVENT_CHUNK: {
      const validated = AgentEventChunkSchema.safeParse(payload)
      if (!validated.success) {
        console.error('Invalid chunk payload: ', validated.error)
        return
      }

      dispatch(
        AgentEventsActions.appendEventChunk({
          conversationId,
          eventId: validated.data.event_id,
          chunk: validated.data.chunk,
        })
      )

      break
    }

    case WS2ReceivedDataType.REFERENCE: {
      const validated = ClientAgentReferenceSchema.safeParse(payload)
      if (!validated.success) {
        console.error('Invalid reference payload: ', validated.error)
        return
      }

      dispatch(AgentReferencesActions.upsertReferences({ references: [validated.data] }))
      break
    }

    case WS2ReceivedDataType.CLOSE_DONE: {
      console.log('CLOSE_DONE: server closed the connection')
      break
    }

    case WS2ReceivedDataType.CLOSE_ERROR_APP: {
      console.error('CLOSE_ERROR_APP: server closed the connection due to an application error (gracefully handled)')
      dispatch(WS2Actions.hideTurnProgress({ conversationId }))

      Sentry.captureException(new Error('Server closed websocket connection due to application error'), {
        extra: {
          conversationId: conversationId,
          ws2ReceivedDataType: WS2ReceivedDataType.CLOSE_ERROR_APP,
        },
      })
      break
    }

    case WS2ReceivedDataType.CLOSE_ERROR_OTHER: {
      console.error('CLOSE_ERROR_OTHER: The server closed the connection due to an unknown or unhandled error.')
      dispatch(WS2Actions.hideTurnProgress({ conversationId }))

      Sentry.captureException(new Error('Server closed websocket connection due an unknown or unhandled error'), {
        extra: {
          conversationId: conversationId,
          ws2ReceivedDataType: WS2ReceivedDataType.CLOSE_ERROR_OTHER,
        },
      })
      break
    }

    case WS2ReceivedDataType.CLOSE_STOP: {
      console.log('CLOSE_STOP: server closed the connection due to a stop request')
      dispatch(WS2Actions.hideTurnProgress({ conversationId }))
      break
    }

    case WS2ReceivedDataType.ERROR: {
      const validated = WS2ErrorPayloadSchema.safeParse(payload)
      if (!validated.success) {
        console.error('Invalid error payload: ', validated.error)
        // Continue with fallback because websocket errors should always surface to the user.
      }

      const message = validated.success ? validated.data.message ?? kErrorGeneratingResponseMessage : kErrorGeneratingResponseMessage
      dispatch(WS2Actions.hideTurnProgress({ conversationId }))
      dispatch(AgentConversationFormActions.setQueryIsSubmitting({ conversationId, isSubmitting: false }))
      dispatch(AgentEventsActions.insertClientErrorEvent({ conversationId, value: message }))
      break
    }

    case WS2ReceivedDataType.ARTIFACT_VERSION_UPDATED: {
      const validated = ArtifactVersionUpdatedSchema.safeParse(payload)
      if (!validated.success) {
        console.error('Invalid ARTIFACT_VERSION_UPDATED payload: ', validated.error)
        return
      }

      const artifactVersion = validated.data.artifact_version

      // Upsert the artifact version into state
      dispatch(ArtifactsActions.upsertArtifactVersion(artifactVersion))
      if (artifactVersion.status === ArtifactVersionStatusType.ERROR || artifactVersion.status === ArtifactVersionStatusType.CANCELLED) {
        dispatch(WS2Actions.hideTurnProgress({ conversationId }))
      }

      // Maps artifact versions to the turns that produced them. Enables downstream artifact-dependents to access turn-content (e.g. references).
      dispatch(ArtifactsActions.setArtifactVersionTurnId({ artifactVersionId: artifactVersion.id, turn_id: validated.data.turn_id }))

      // Update the artifact_version_id of the event to associate it with the artifact version
      dispatch(
        AgentEventsActions.updateArtifactVersionId({
          conversationId: conversationId,
          eventId: validated.data.response_event_id,
          artifactVersionId: artifactVersion.id,
        })
      )

      const isActiveConversation = conversationId === selectActiveConversationId(getState())

      // Open the artifact version in the content viewer if:
      // - The artifact version is completed
      // - This conversation is actively being viewed
      if (ArtifactVersionStatusType.COMPLETE === artifactVersion.status && isActiveConversation) {
        const isConversationContentViewerOpen = selectAgentConversationHasActiveContentViewer(getState(), { conversationId })

        // If the content viewer is open, clear it first
        if (isConversationContentViewerOpen) {
          dispatch(ContentViewerActions.clearContentViewer())
          dispatch(AgentConversationsActions.removeConversationActiveContentViewer({ conversationId }))
        }

        // Set a delay only if the content viewer was open (to allow time for it to close before re-opening)
        const delay = isConversationContentViewerOpen ? 800 : 0

        // Open the new artifact version in the content viewer
        setTimeout(() => {
          // Guard: user may have switched conversations during the close/reopen delay
          const stillActive = conversationId === selectActiveConversationId(getState())
          if (!stillActive) return

          const session = buildArtifactViewerSession({ artifactVersion, conversationId, autoOpen: true })
          dispatch(ContentViewerActions.setContentViewerSession({ session }))
          dispatch(AgentConversationsActions.setConversationActiveContentViewerSession({ conversationId, session }))
        }, delay)
      }

      break
    }

    case WS2ReceivedDataType.STATUS: {
      const validated = WS2StatusPayloadSchema.safeParse(payload)
      if (!validated.success) {
        console.error('Invalid status payload: ', validated.error)
        return
      }

      dispatch(WS2Actions.appendTurnProgressMessage({ conversationId, message: validated.data.message }))
      break
    }

    default: {
      console.error('Unknown stream payload type: ', type)
    }
  }
}
